"use client"

import { useState } from "react"
import { FormGroup, Label, Input, Button, Row, Col, Card, CardBody, Badge } from "reactstrap"
import { Edit2, CheckCircle, PlusCircle, Clock, X } from "react-feather"
import { toast } from "react-toastify"
import "./WorkingHoursForm.css"

const WorkingHoursForm = ({ workingHours, setWorkingHours, editMode }) => {
    const [editingIndex, setEditingIndex] = useState(null)
    const [updatedHours, setUpdatedHours] = useState([...workingHours])
    const [newEntry, setNewEntry] = useState({
        day: "",
        openAt: "",
        closedAt: "",
        isClosed: false,
    })

    const daysOfWeek = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"]

    const availableDays = daysOfWeek.filter((day) => !updatedHours.some((entry) => entry.day === day))

    const handleChange = (index, field, value) => {
        const updatedData = [...updatedHours]

        if (field === "isClosed") {
            const isNowClosed = value
            updatedData[index] = {
                ...updatedData[index],
                isClosed: isNowClosed,
                ...(isNowClosed ? { openAt: "", closedAt: "" } : {}),
            }
        } else {
            updatedData[index][field] = value
        }

        setUpdatedHours(updatedData)
    }

    const handleNewEntryChange = (field, value) => {
        setNewEntry((prev) => ({
            ...prev,
            [field]: value,
            ...(field === "isClosed" && value ? { openAt: "", closedAt: "" } : {}),
        }))
    }

    const validateNewEntry = () => {
        if (!newEntry.day) {
            toast.warn("Please select a day.")
            return false
        }
        if (updatedHours.some((entry) => entry.day === newEntry.day)) {
            toast.warn("This day is already added.")
            return false
        }
        if (!newEntry.isClosed) {
            if (!newEntry.openAt || !newEntry.closedAt) {
                toast.warn("Please provide both opening and closing times.")
                return false
            }
            if (newEntry.openAt >= newEntry.closedAt) {
                toast.warn("Opening time must be before closing time.")
                return false
            }
        }
        return true
    }

    const handleAddNewEntry = () => {
        if (updatedHours.length >= 7) {
            toast.warn("Cannot add more than 7 days.")
            return
        }

        if (!validateNewEntry()) {
            return
        }

        const newHours = [
            ...updatedHours,
            {
                day: newEntry.day,
                openAt: newEntry.isClosed ? "" : newEntry.openAt,
                closedAt: newEntry.isClosed ? "" : newEntry.closedAt,
                isClosed: newEntry.isClosed,
            },
        ]

        setUpdatedHours(newHours)
        setWorkingHours(newHours)
        setNewEntry({ day: "", openAt: "", closedAt: "", isClosed: false })
        toast.success("Working hours added successfully!")
    }

    const handleSave = (index) => {
        const entry = updatedHours[index]
        if (!entry.isClosed) {
            if (!entry.openAt || !entry.closedAt) {
                toast.warn("Please provide both opening and closing times.")
                return
            }
            if (entry.openAt >= entry.closedAt) {
                toast.warn("Opening time must be before closing time.")
                return
            }
        }

        setEditingIndex(null)
        setWorkingHours(updatedHours)
        toast.success("Working hours updated successfully!")
    }

    const handleRemoveEntry = (index) => {
        const newHours = updatedHours.filter((_, i) => i !== index)
        setUpdatedHours(newHours)
        setWorkingHours(newHours)
        setEditingIndex(null)
        toast.success("Working hours removed successfully!")
    }

    const formatTime = (time) => {
        if (!time) return ""
        const [hours, minutes] = time.split(":")
        const hour = Number.parseInt(hours)
        const ampm = hour >= 12 ? "PM" : "AM"
        const displayHour = hour % 12 || 12
        return `${displayHour}:${minutes} ${ampm}`
    }

    return (
        <div className="working-hours-container">
            {/* Existing Working Hours */}
            {updatedHours.length > 0 && (
                <div className="working-hours-list">
                    {updatedHours.map((entry, index) => {
                        const isEditing = editingIndex === index

                        return (
                            <Card key={index} className="working-hours-card mb-3">
                                <CardBody className="p-3">
                                    {isEditing ? (
                                        // Edit Mode
                                        <Row className="align-items-center">
                                            <Col md={3}>
                                                <div className="day-display">
                                                    <Clock size={16} className="me-2 text-primary" />
                                                    <span className="fw-bold">{entry.day}</span>
                                                </div>
                                            </Col>
                                            <Col md={2}>
                                                <FormGroup className="mb-0">
                                                    <div className="custom-checkbox-wrapper-small">
                                                        <label className="custom-checkbox-label-small">
                                                            <input
                                                                type="checkbox"
                                                                checked={entry.isClosed}
                                                                onChange={(e) => handleChange(index, "isClosed", e.target.checked)}
                                                                className="custom-checkbox-input-small"
                                                            />
                                                            <span className="custom-checkbox-checkmark-small"></span>
                                                            <span className="ms-2">Closed</span>
                                                        </label>
                                                    </div>
                                                </FormGroup>
                                            </Col>
                                            {!entry.isClosed && (
                                                <>
                                                    <Col md={3}>
                                                        <FormGroup className="mb-0">
                                                            <Label className="form-label-small">Open At</Label>
                                                            <Input
                                                                type="time"
                                                                value={entry.openAt || ""}
                                                                onChange={(e) => handleChange(index, "openAt", e.target.value)}
                                                                className="modern-input-small"
                                                            />
                                                        </FormGroup>
                                                    </Col>
                                                    <Col md={3}>
                                                        <FormGroup className="mb-0">
                                                            <Label className="form-label-small">Close At</Label>
                                                            <Input
                                                                type="time"
                                                                value={entry.closedAt || ""}
                                                                onChange={(e) => handleChange(index, "closedAt", e.target.value)}
                                                                className="modern-input-small"
                                                            />
                                                        </FormGroup>
                                                    </Col>
                                                </>
                                            )}
                                            <Col md={1}>
                                                <div className="d-flex gap-1">
                                                    <Button
                                                        color="success"
                                                        size="sm"
                                                        onClick={() => handleSave(index)}
                                                        className="action-btn-small"
                                                    >
                                                        <CheckCircle size={14} />
                                                    </Button>
                                                    <Button
                                                        color="danger"
                                                        size="sm"
                                                        onClick={() => setEditingIndex(null)}
                                                        className="action-btn-small"
                                                    >
                                                        <X size={14} />
                                                    </Button>
                                                </div>
                                            </Col>
                                        </Row>
                                    ) : (
                                        // Display Mode
                                        <Row className="align-items-center">
                                            <Col md={3}>
                                                <div className="day-display">
                                                    <Clock size={16} className="me-2 text-primary" />
                                                    <span className="fw-bold">{entry.day}</span>
                                                </div>
                                            </Col>
                                            <Col md={6}>
                                                {entry.isClosed ? (
                                                    <Badge color="secondary" className="status-badge">
                                                        Closed
                                                    </Badge>
                                                ) : (
                                                    <div className="time-display">
                                                        <span className="time-text">
                                                            {formatTime(entry.openAt)} - {formatTime(entry.closedAt)}
                                                        </span>
                                                    </div>
                                                )}
                                            </Col>
                                            <Col md={3}>
                                                {editMode && (
                                                    <div className="d-flex gap-2 justify-content-end">
                                                        <Button
                                                            color="primary"
                                                            size="sm"
                                                            onClick={() => setEditingIndex(index)}
                                                            className="action-btn-small"
                                                        >
                                                            <Edit2 size={14} />
                                                        </Button>
                                                        <Button
                                                            color="danger"
                                                            size="sm"
                                                            onClick={() => handleRemoveEntry(index)}
                                                            className="action-btn-small"
                                                        >
                                                            <X size={14} />
                                                        </Button>
                                                    </div>
                                                )}
                                            </Col>
                                        </Row>
                                    )}
                                </CardBody>
                            </Card>
                        )
                    })}
                </div>
            )}

            {/* Add New Working Hours */}
            {editMode && updatedHours.length < 7 && (
                <Card className="add-hours-card">
                    <CardBody>
                        <div className="add-hours-header">
                            <PlusCircle size={18} className="me-2 text-primary" />
                            <h6 className="mb-0">Add Working Hours</h6>
                        </div>
                        <hr className="my-3" />
                        <Row className="align-items-center">
                            <Col md={3}>
                                <FormGroup>
                                    <Label className="form-label-modern">Day</Label>
                                    <Input
                                        type="select"
                                        value={newEntry.day}
                                        onChange={(e) => handleNewEntryChange("day", e.target.value)}
                                        className="modern-select"
                                    >
                                        <option value="">Select Day</option>
                                        {availableDays.map((day) => (
                                            <option key={day} value={day}>
                                                {day}
                                            </option>
                                        ))}
                                    </Input>
                                </FormGroup>
                            </Col>
                            <Col md={2}>
                                <FormGroup>
                                    <Label className="form-label-modern"></Label>
                                    <div className="custom-checkbox-wrapper">
                                        <label className="custom-checkbox-label">
                                            <input
                                                style={{
                                                    marginRight: "3px"
                                                }}
                                                type="checkbox"
                                                checked={newEntry.isClosed}
                                                onChange={(e) => handleNewEntryChange("isClosed", e.target.checked)}
                                                className="custom-checkbox-input"
                                            />
                                            <span className="custom-checkbox-checkmark"></span>
                                            <span className="ms-2">Closed</span>
                                        </label>
                                    </div>
                                </FormGroup>
                            </Col>
                            {!newEntry.isClosed && (
                                <>
                                    <Col md={3}>
                                        <FormGroup>
                                            <Label className="form-label-modern">Open At</Label>
                                            <Input
                                                type="time"
                                                value={newEntry.openAt}
                                                onChange={(e) => handleNewEntryChange("openAt", e.target.value)}
                                                className="modern-input"
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md={3}>
                                        <FormGroup>
                                            <Label className="form-label-modern">Close At</Label>
                                            <Input
                                                type="time"
                                                value={newEntry.closedAt}
                                                onChange={(e) => handleNewEntryChange("closedAt", e.target.value)}
                                                className="modern-input"
                                            />
                                        </FormGroup>
                                    </Col>
                                </>
                            )}
                            <Col md={1}>
                                <Label className="form-label-modern"></Label>
                                <Button
                                    color="primary"
                                    onClick={handleAddNewEntry}
                                    disabled={updatedHours.length >= 7}
                                    className="add-btn"
                                >
                                    <PlusCircle size={16} />
                                </Button>
                            </Col>
                        </Row>
                    </CardBody>
                </Card>
            )}

            {/* Empty State */}
            {updatedHours.length === 0 && !editMode && (
                <Card className="empty-state-card">
                    <CardBody className="text-center py-4">
                        <Clock size={48} className="text-muted mb-3" />
                        <h6 className="text-muted">No working hours configured</h6>
                        <p className="text-muted mb-0">Working hours will be displayed here once configured.</p>
                    </CardBody>
                </Card>
            )}
        </div>
    )
}

export default WorkingHoursForm
